import { SubmittalStatusToStrMap } from "../../../../constants";
import {
  TActivitiesV3,
  TLinkedSubmittal,
  TLinkedMaterial,
  TMaterialImpactAnalysis
} from "./models";
import { getRiskAssessmentForEntity } from "./utils";

const WF_STATUS_LABEL_MAP: Record<string, string> = {
  WF_NOT_STARTED: "Not Started",
  IN_PROGRESS: "In Progress",
  WF_COMPLETED: "Completed",
  VOID: "Void",
  COMPLETED: "Completed"
};

function getReadableStatus(
  dateBlockMilestoneState: string | undefined,
  workflowStatus: string | undefined
): string {
  if (dateBlockMilestoneState) {
    return (
      WF_STATUS_LABEL_MAP[dateBlockMilestoneState] || dateBlockMilestoneState
    );
  }
  if (workflowStatus) {
    return WF_STATUS_LABEL_MAP[workflowStatus] || workflowStatus;
  }
  return "";
}

function getSubmittalStatusFromEnrichment(enrichment: any): string {
  if (enrichment?.workflow_status != null) {
    const mapped = SubmittalStatusToStrMap[enrichment.workflow_status];
    if (mapped) return mapped;
  }
  const dateBlocks = enrichment?.date_block_submittals;
  if (dateBlocks?.length > 0 && dateBlocks[0]?.milestone_state) {
    return dateBlocks[0].milestone_state;
  }
  return "";
}

// Mirrors the on-screen submittalStatusRenderer cascade so the exported
// `status` column matches what users see in the grid.
function deriveSubmittalStatus(
  enrichment: any,
  sdbNew: any,
  submittal: TLinkedSubmittal
): string {
  const fromEnrichment = getSubmittalStatusFromEnrichment(enrichment);
  if (fromEnrichment) return fromEnrichment;

  if (sdbNew?.workflow_status_id != null) {
    const mapped = SubmittalStatusToStrMap[sdbNew.workflow_status_id];
    if (mapped) return mapped;
  }

  return getReadableStatus(
    submittal.dateBlockData?.milestone_state,
    submittal.workflow_status
  );
}

function getMaterialStatusFromEnrichment(enrichment: any): string {
  const dateBlocks = enrichment?.date_block_materials;
  if (dateBlocks?.length > 0 && dateBlocks[0]?.milestone_state) {
    return dateBlocks[0].milestone_state;
  }
  return "";
}

export type TSubmittalGridRow = {
  id: string;
  risk_level: string;
  risk_assessment: string;
  risk_before: string;
  risk_after: string;
  submittal_id: string;
  submittal_sequence_id: string;
  spec_section: string;
  spec_name: string;
  title: string;
  responsible_contractor: string;
  gc_reviewer: string;
  status: string;
  next_milestone_date: string | null;
  final_deadline: string | null;
  linked_materials: number;
  linked_activity: string;
  change_in_float: number | null;
  delta: number | null;
  float_before: number | string;
  float_after: number | string;
  _submittal: TLinkedSubmittal;
  _activity: TActivitiesV3;
  _enrichment?: any;
  _sdbNew?: any;
  _vizSubmittalData?: any;
};

export type TMaterialGridRow = {
  id: string;
  risk_level: string;
  risk_assessment: string;
  risk_before: string;
  risk_after: string;
  material_id: string;
  material_sequence_id: string;
  spec_section: string;
  spec_name: string;
  title: string;
  responsible_contractor: string;
  status: string;
  next_milestone_date: string | null;
  final_deadline: string | null;
  linked_submittals: number;
  linked_activity: string;
  change_in_float: number | null;
  delta: number | null;
  float_before: number | string;
  float_after: number | string;
  material_tag: string;
  gc_representative: string;
  is_implicit: boolean;
  _material: TLinkedMaterial;
  _activity: TActivitiesV3;
  _enrichment?: any;
};

function getFloatFromGoverningTask(
  governingTasks: any[] | undefined
): number | string {
  if (!governingTasks?.length) return "";
  const task = governingTasks[0];
  return task?.total_slack != null ? Math.floor(task.total_slack / 8) : "";
}

function getLinkedToEndDate(
  entity: TLinkedSubmittal | TLinkedMaterial,
  activityId: string
): boolean {
  const tasks = entity.old_governing_task || [];
  return (
    tasks.find((t) => t.activity_id === activityId)?.linked_to_end_date || false
  );
}

function getDelta(
  activity: TActivitiesV3,
  linkedToEndDate: boolean
): number | null {
  return linkedToEndDate
    ? (activity.end_date_delta ?? null)
    : (activity.delta ?? null);
}

export function formatChangeInFloat(value: number | null | undefined): string {
  if (value == null) return "";
  if (value > 0) return `+${value}`;
  return `${value}`;
}

export type TSubmittalEnrichment = Record<string, any>;
export type TMaterialEnrichment = Record<string, any>;

// Drops `_enrichment`, `_sdbNew`, `_vizSubmittalData` from each row.
// The print route only needs `_submittal`/`_material` + `_activity` for
// diagram drawing; keeping the rest holds 4–5 MB of nested closures
// across 1000+ rows.
export function stripHeavyFieldsForPrint<
  T extends TSubmittalGridRow | TMaterialGridRow
>(rows: T[]): T[] {
  return rows.map((row) => {
    const next = { ...row } as any;
    delete next._enrichment;
    delete next._sdbNew;
    delete next._vizSubmittalData;
    return next as T;
  });
}

const asStringOrNull = (value: any) => (value ? `${value}` : null);

// A governing material's own governing task, when present, supersedes the
// submittal's governing task for float.
const floatFromGoverningMaterial = (
  governingMaterial: any,
  fallback: number | string
): number | string => {
  if (!governingMaterial?.length) return fallback;
  const matTask = governingMaterial[0]?.governing_task;
  if (!matTask?.length) return fallback;
  return matTask[0]?.total_slack == null
    ? ""
    : Math.floor(matTask[0].total_slack / 8);
};

const resolveGcReviewer = (enrichment: any, sdbNew: any, sdbOld: any) => {
  const parts = [
    enrichment?.gc_reviewer_first_name,
    enrichment?.gc_reviewer_last_name
  ].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  return sdbNew?.gc_reviewer || sdbOld?.gc_reviewer || "";
};

const buildVizSubmittalData = (sdbNew: any) =>
  sdbNew && sdbNew.workflow_status_id != null
    ? {
        date_block_submittals: [sdbNew],
        workflow_status: sdbNew.workflow_status_id,
        revision: sdbNew.revision ?? 0,
        revision_date_blocks: sdbNew.revision_date_blocks ?? []
      }
    : null;

function buildSubmittalGridRow(
  submittal: TLinkedSubmittal,
  activity: any,
  category: string,
  enrichmentMap?: Record<string, TSubmittalEnrichment>
): TSubmittalGridRow {
  const sdbOld = submittal.SDB?.old;
  const sdbNew = submittal.SDB?.new;
  const linkedToEndDate = getLinkedToEndDate(submittal, activity.id);

  // Get governing materials count
  const oldGovMaterials =
    (submittal as any).oldGoverningMaterials ||
    submittal.old_governing_material ||
    [];
  const newGovMaterials =
    (submittal as any).newGoverningMaterials ||
    submittal.new_governing_material ||
    [];
  const materialCount = Math.max(
    oldGovMaterials.length,
    newGovMaterials.length
  );

  // Float from governing tasks or governing materials
  const floatBefore = floatFromGoverningMaterial(
    submittal.old_governing_material,
    getFloatFromGoverningTask(submittal.old_governing_task)
  );
  const floatAfter = floatFromGoverningMaterial(
    submittal.new_governing_material,
    getFloatFromGoverningTask(submittal.new_governing_task)
  );

  const nextMilestone =
    sdbNew?.SI_calculations?.SI_PRJNMD ||
    sdbNew?.SI_calculations?.SI_PNMD ||
    null;

  const finalDeadline = sdbNew?.SI_calculations?.SI_FDD || null;

  const riskAssessment = getRiskAssessmentForEntity(submittal?.SDB);
  // Prefer inline enrichment from schedule impact API, fall back to separate report API
  const enrichment =
    (submittal as any).enrichment || enrichmentMap?.[submittal.submittal_id];

  // Status priority: mirrors the on-screen submittalStatusRenderer
  // cascade so Excel/CSV exports carry the same text users see.
  const submittalStatus = deriveSubmittalStatus(enrichment, sdbNew, submittal);

  const isDeleted = category === "DELETED";
  return {
    id: `${category}-${activity.id}-${submittal.submittal_id}`,
    risk_level: category,
    risk_assessment: riskAssessment?.text || "",
    risk_before: sdbOld?.risk_assessment?.level || "",
    risk_after: isDeleted ? "" : sdbNew?.risk_assessment?.level || "",
    submittal_id: submittal.submittal_id,
    submittal_sequence_id: asStringOrNull(sdbOld?.submittal_sequence_id) ?? "",
    spec_section: sdbOld?.spec_no || "",
    spec_name: enrichment?.spec_section_name || sdbOld?.spec_name || "",
    title: sdbOld?.title || "",
    responsible_contractor:
      enrichment?.responsible_contractor_name ||
      sdbNew?.responsible_contractor ||
      sdbOld?.responsible_contractor ||
      "",
    gc_reviewer: resolveGcReviewer(enrichment, sdbNew, sdbOld),
    status: submittalStatus,
    next_milestone_date: asStringOrNull(nextMilestone),
    final_deadline: isDeleted ? null : asStringOrNull(finalDeadline),
    linked_materials: materialCount,
    linked_activity: `${activity.id} - ${activity.text}`,
    change_in_float: isDeleted ? null : (sdbNew?.change_in_float ?? null),
    delta: isDeleted ? null : getDelta(activity, linkedToEndDate),
    float_before: floatBefore,
    float_after: floatAfter,
    _submittal: submittal,
    _activity: activity,
    _enrichment: enrichment || null,
    _sdbNew: sdbNew || null,
    _vizSubmittalData: buildVizSubmittalData(sdbNew)
  } as TSubmittalGridRow;
}

export function flattenSubmittalsForGrid(
  sia: TMaterialImpactAnalysis,
  showDeletedActivities = true,
  enrichmentMap?: Record<string, TSubmittalEnrichment>
): TSubmittalGridRow[] {
  const rows: TSubmittalGridRow[] = [];
  if (!sia) return rows;

  const categories = Object.keys(sia) as Array<keyof TMaterialImpactAnalysis>;
  categories.forEach((category) => {
    if (!showDeletedActivities && category === "DELETED") return;

    const activities = sia[category]?.activities;
    if (!activities) return;

    activities.forEach((activity) => {
      const linkedSubmittals = activity.linked_submittals || [];
      linkedSubmittals.forEach((submittal: TLinkedSubmittal) => {
        rows.push(
          buildSubmittalGridRow(
            submittal,
            activity,
            category as string,
            enrichmentMap
          )
        );
      });
    });
  });

  return rows;
}

export function flattenMaterialsForGrid(
  mia: TMaterialImpactAnalysis,
  showDeletedActivities = true,
  enrichmentMap?: Record<string, TMaterialEnrichment>
): TMaterialGridRow[] {
  const rows: TMaterialGridRow[] = [];
  if (!mia) return rows;

  const categories = Object.keys(mia) as Array<keyof TMaterialImpactAnalysis>;
  categories.forEach((category) => {
    if (!showDeletedActivities && category === "DELETED") return;

    const activities = mia[category]?.activities;
    if (!activities) return;

    activities.forEach((activity) => {
      const linkedMaterials = activity.linked_materials || [];
      linkedMaterials.forEach((material: TLinkedMaterial) => {
        const mdbOld = material.MDB?.old;
        const mdbNew = material.MDB?.new;
        const linkedToEndDate = getLinkedToEndDate(material, activity.id);
        const isImplicit = mdbOld?.implicit || false;

        const floatBefore = getFloatFromGoverningTask(
          material.old_governing_task
        );
        const floatAfter = getFloatFromGoverningTask(
          material.new_governing_task
        );

        const nextMilestone =
          mdbNew?.SI_calculations?.SI_PRJNMD ||
          mdbNew?.SI_calculations?.SI_PNMD ||
          null;

        const finalDeadline = mdbNew?.SI_calculations?.SI_FDD || null;

        const linkedSubmittalCount = material.linked_submittals?.length || 0;

        let title = mdbOld?.name || "";
        if (isImplicit) {
          title = material.name
            ? `Material for Submittal ${material.name}`
            : "";
        }

        const riskAssessment = getRiskAssessmentForEntity(material?.MDB);
        // Prefer inline enrichment from schedule impact API, fall back to separate report API
        const enrichment =
          (material as any).enrichment || enrichmentMap?.[material.material_id];

        const gcRepParts = [
          enrichment?.gc_representative_first_name,
          enrichment?.gc_representative_last_name
        ].filter(Boolean);
        const gcRepresentative =
          gcRepParts.length > 0
            ? gcRepParts.join(" ")
            : mdbNew?.gc_reviewer || mdbOld?.gc_reviewer || "";

        // Status priority: enrichment (report API) > schedule impact API fallback
        const materialStatus =
          getMaterialStatusFromEnrichment(enrichment) ||
          getReadableStatus(
            material.dateBlockData?.milestone_state,
            material.workflow_status
          );

        const isDeleted = category === "DELETED";
        rows.push({
          id: `${category}-${activity.id}-${material.material_id}`,
          risk_level: category as string,
          risk_assessment: riskAssessment?.text || "",
          risk_before: mdbOld?.risk_assessment?.level || "",
          risk_after: isDeleted ? "" : mdbNew?.risk_assessment?.level || "",
          material_id: material.material_id,
          material_sequence_id: mdbOld?.material_sequence_id
            ? `${mdbOld.material_sequence_id}`
            : "",
          spec_section: enrichment?.spec_section_no || mdbOld?.spec_no || "",
          spec_name: enrichment?.spec_section_name || mdbOld?.spec_name || "",
          title,
          responsible_contractor:
            enrichment?.trade_partner_name ||
            mdbNew?.responsible_contractor ||
            mdbOld?.responsible_contractor ||
            "",
          material_tag: enrichment?.material_tag || "",
          gc_representative: gcRepresentative,
          status: materialStatus,
          next_milestone_date: nextMilestone ? `${nextMilestone}` : null,
          final_deadline: isDeleted
            ? null
            : finalDeadline
              ? `${finalDeadline}`
              : null,
          linked_submittals: linkedSubmittalCount,
          linked_activity: `${activity.id} - ${activity.text}`,
          change_in_float: isDeleted ? null : (mdbNew?.change_in_float ?? null),
          delta: isDeleted ? null : getDelta(activity, linkedToEndDate),
          float_before: floatBefore,
          float_after: floatAfter,
          is_implicit: isImplicit,
          _material: material,
          _activity: activity,
          _enrichment: enrichment || null
        });
      });
    });
  });

  return rows;
}
